import { useState, useEffect, useCallback } from "react";
import {
  FiX,
  FiPhone,
  FiCreditCard,
  FiSearch,
  FiInfo,
  FiAlertTriangle,
} from "react-icons/fi";
import { authApi } from "@/api/api";
import { billingApi } from "@/api/billingApi";
import {
  ensurePhoneNumberFormat,
  formatPhoneNumberForDisplay,
} from "@/utils/phoneNumberUtils";
import { validateZipcode } from "@/utils/validation";
import toast from "react-hot-toast";
import { billingApiHooks } from "@/api/billingApi";
import { businessLinesHooks } from "@/api/businessLinesApi";
import { usePermissions } from "@/contexts/PermissionContext";
import usFlagImage from "@/assets/images/usflag.png";

// Types
interface RegularPhoneNumber {
  full_number: string;
  rate_center: string;
  country_code: string;
  city: string;
  state: string;
}

type TollFreeNumber = string;

interface CallPackage {
  currency: string;
  fee_per_overage_minute: number;
  id: string;
  minutes: number;
  name: string;
  price: number;
  recurring: {
    interval: string;
    interval_count: number;
  };
  type: string;
}

interface AddNewLineDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newLine?: any) => void;
}

export default function AddNewLineDialog({
  isOpen,
  onClose,
  onSuccess,
}: AddNewLineDialogProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [regularNumbers, setRegularNumbers] = useState<RegularPhoneNumber[]>(
    []
  );
  const [tollFreeNumbers, setTollFreeNumbers] = useState<TollFreeNumber[]>([]);
  const [selectedType, setSelectedType] = useState<"regular" | "toll_free">(
    "regular"
  );
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [callPackages, setCallPackages] = useState<CallPackage[]>([]);
  const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);
  const [selectedMinutes, setSelectedMinutes] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [zipcode, setZipcode] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [reservationData, setReservationData] = useState<any>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [paymentIssueError, setPaymentIssueError] = useState<string | null>(
    null
  );
  const [showPaymentIssueDialog, setShowPaymentIssueDialog] = useState(false);

  const { permissionLevel } = usePermissions();

  // Only call payment methods API if user is owner to avoid 403 error for other users
  const { data: paymentMethodsData, isLoading: isPaymentMethodsLoading } =
    billingApiHooks.usePaymentMethods({
      enabled: permissionLevel === "owner",
    });

  const { refetch: refetchBusinessLines } =
    businessLinesHooks.useActiveBusinessLines();

  // Functions to handle reservation data with dynamic imports
  const fetchReservationData = async () => {
    try {
      const { packagesApi } = await import("@/api/packagesApi");
      const response = await packagesApi.getReservation();
      setReservationData(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching reservation:", error);
      setReservationData(null);
      return null;
    }
  };

  const deleteReservation = async () => {
    try {
      const { packagesApi } = await import("@/api/packagesApi");
      await packagesApi.deleteReservation();
      setReservationData(null);
      return true;
    } catch (error) {
      console.error("Error deleting reservation:", error);
      return false;
    }
  };

  // Check for payment issues when dialog opens
  const checkPaymentIssue = async () => {
    try {
      await billingApi.getPaymentIssue();
      // If no error, clear any previous payment issue state
      setPaymentIssueError(null);
      setShowPaymentIssueDialog(false);
    } catch (error: any) {
      console.log("Payment issue check error:", error);
      if (error?.response?.status === 402) {
        const errorMessage =
          error?.response?.data?.detail ||
          "This operation is not allowed. You have a past due subscription. Please resolve the payment issue.";
        setPaymentIssueError(errorMessage);
        setShowPaymentIssueDialog(true);
      }
    }
  };

  // delete later just for testing
  const testRegularNumbers = [
    {
      full_number: "+19495292627",
      rate_center: "tehran",
      country_code: "ir",
      city: "tehran",
      state: "tehran",
    },
    {
      full_number: "+13373859858",
      rate_center: "tehran",
      country_code: "ir",
      city: "tehran",
      state: "tehran",
    },
    {
      full_number: "+13375950009",
      rate_center: "tehran",
      country_code: "ir",
      city: "tehran",
      state: "tehran",
    },
  ];
  const testTollFreeNumbers = ["8334174691", "8334174692", "8334174693"];
  // delete later just for testing

  // Helper: is there a reservation?
  const reservation =
    reservationData && reservationData.reservation_id ? reservationData : null;

  // Handler: pay for reservation
  const handleReservationPay = async () => {
    if (!reservation) return;
    setIsLoading(true);
    try {
      const subscriptionResponse = await billingApi.pay(
        reservation.reservation_id
      );
      // Extract subscription_id from response URL
      // const url = subscriptionResponse.url;
      // const urlParams = new URLSearchParams(url.split("?")[1]);
      // const subId = urlParams.get("subscription_id");
      // if (subId) {
      //   setSubscriptionId(subId);
      //   setReservationId(reservation.reservation_id);
      //   setCurrentStep(4); // Go to step 4
      // } else {
      //   toast.error("Failed to get subscription id");
      // }
      console.log("subscriptionResponse", subscriptionResponse);

      // Check if payment was successful based on response message
      if (subscriptionResponse?.detail?.msg === "Payment successful.") {
        setReservationId(reservation.reservation_id);
        setCurrentStep(4); // Go to step 4
        toast.success("Payment successful!");
      } else {
        toast.error("Payment failed. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to create subscription");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper: create business line from reservation data
  const createBusinessLineFromReservation = async () => {
    try {
      // Import the packages API to get reservation data
      const { packagesApi } = await import("@/api/packagesApi");

      // Get the reservation data using the reservation ID
      const reservationResponse = await packagesApi.getReservation();

      if (
        reservationResponse?.data &&
        reservationResponse.data.business_line_id
      ) {
        console.log("Using reservation data:", reservationResponse.data);

        // Create business line using real reservation data
        const newBusinessLine = {
          id: reservationResponse.data.business_line_id, // Real business line ID
          business: "New Business Line", // Will be updated when fetched from server
          business_hour_data: {
            timezone: "America/New_York",
            working_hours: {
              monday: {
                is_open: true,
                open_time: "09:00",
                close_time: "17:00",
              },
              tuesday: {
                is_open: true,
                open_time: "09:00",
                close_time: "17:00",
              },
              wednesday: {
                is_open: true,
                open_time: "09:00",
                close_time: "17:00",
              },
              thursday: {
                is_open: true,
                open_time: "09:00",
                close_time: "17:00",
              },
              friday: {
                is_open: true,
                open_time: "09:00",
                close_time: "17:00",
              },
              saturday: {
                is_open: false,
                open_time: "09:00",
                close_time: "17:00",
              },
              sunday: {
                is_open: false,
                open_time: "09:00",
                close_time: "17:00",
              },
            },
          },
          is_default: false,
          line_type: reservationResponse.data.package?.type || selectedType, // Use package type from reservation
          note: { content: null, expires_at: null },
          phone_number: reservationResponse.data.phone_number, // Real phone number from reservation
          profile: { business_name: null, location: null },
          recording_enabled: false,
          service_type: reservationResponse.data.service_type || "answering", // Real service type
          status: "pending-activation" as const,
        };

        console.log(
          "Created business line from reservation data:",
          newBusinessLine
        );
        return newBusinessLine;
      } else {
        throw new Error("No reservation data available");
      }
    } catch (error) {
      console.error("Error getting reservation data:", error);
      throw error;
    }
  };

  // Handler: delete reservation
  const handleReservationDelete = async () => {
    if (!reservation) return;
    setIsLoading(true);
    try {
      const success = await deleteReservation();
      if (success) {
        toast.success("Reservation deleted");
        await fetchReservationData();
      } else {
        toast.error("Failed to delete reservation");
      }
      // Clear all reservation/payment state and close dialog
      setSubscriptionId(null);
      setReservationId(null);
      setCurrentStep(1);
      setSelectedNumber(null);
      setSelectedPackageIndex(0);
      setSelectedMinutes(0);
      setCallPackages([]);
      onClose();
    } catch (error) {
      toast.error("Failed to delete reservation");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler: pay with subscription_id and reservation_id
  const handlePayWithSubscription = async () => {
    if (!subscriptionId || !reservationId) return;
    setIsLoading(true);
    try {
      await billingApi.createSubscriptionItem(subscriptionId, reservationId);
      toast.success("Payment successful");

      // After successful payment, create business line using reservation data
      let newBusinessLine = null;
      try {
        // Try to create business line from reservation data first
        newBusinessLine = await createBusinessLineFromReservation();
      } catch (reservationError) {
        console.error("Error getting reservation data:", reservationError);

        // Fallback: try to fetch from business lines API
        try {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const allLines = await businessLinesHooks.useActiveBusinessLines()
            .data;

          newBusinessLine = allLines.find(
            (line: any) => line.phone_number === selectedNumber
          );

          if (newBusinessLine) {
            console.log("Found new business line from API:", newBusinessLine);
          } else {
            throw new Error("Business line not found in API");
          }
        } catch (fetchError) {
          console.error(
            "Error fetching business line after payment:",
            fetchError
          );
          // Final fallback: create temporary business line
          newBusinessLine = {
            id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            business: "New Business Line",
            business_hour_data: {
              timezone: "America/New_York",
              working_hours: {
                monday: {
                  is_open: true,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                tuesday: {
                  is_open: true,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                wednesday: {
                  is_open: true,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                thursday: {
                  is_open: true,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                friday: {
                  is_open: true,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                saturday: {
                  is_open: false,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                sunday: {
                  is_open: false,
                  open_time: "09:00",
                  close_time: "17:00",
                },
              },
            },
            is_default: false,
            line_type: selectedType,
            note: { content: null, expires_at: null },
            phone_number: selectedNumber,
            profile: { business_name: null, location: null },
            recording_enabled: false,
            service_type: "answering" as const,
            status: "pending-activation" as const,
          };
        }
      }

      onSuccess(newBusinessLine);

      // Wait 10 seconds then refetch business lines
      setTimeout(() => {
        refetchBusinessLines();
      }, 10000);

      onClose();
    } catch (error) {
      toast.error("Failed to complete payment");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchValue: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (searchValue.trim()) {
            const validation = validateZipcode(searchValue.trim());
            if (validation.isValid) {
              setZipcode(searchValue.trim());
              fetchRegularNumbers(searchValue.trim());
            }
          } else {
            setZipcode("");
            fetchRegularNumbers();
          }
        }, 500); // 500ms delay
      };
    })(),
    []
  );

  // Format zipcode input with masking
  const formatZipcodeInput = (value: string) => {
    // Remove all non-digits
    const digitsOnly = value.replace(/\D/g, "");

    // Apply formatting based on length
    if (digitsOnly.length <= 5) {
      return digitsOnly;
    } else if (digitsOnly.length <= 9) {
      return `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5)}`;
    } else {
      // Limit to 9 digits total
      return `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5, 9)}`;
    }
  };

  // Handle zipcode input change with masking
  const handleZipcodeChange = (value: string) => {
    const formattedValue = formatZipcodeInput(value);
    setZipcode(formattedValue);
    debouncedSearch(formattedValue);
  };

  // Fetch numbers when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Check for payment issues first
      checkPaymentIssue();
      fetchReservationData();
      fetchRegularNumbers();
      fetchTollFreeNumbers();
    } else {
      // Reset state when dialog closes
      setCurrentStep(1);
      setSelectedNumber(null);
      setSelectedPackageIndex(0);
      setSelectedMinutes(0);
      setCallPackages([]);
      setSubscriptionId(null);
      setReservationId(null);
      setZipcode("");
      setPaymentIssueError(null);
      setShowPaymentIssueDialog(false);
    }
  }, [isOpen]);

  // Fetch packages when number is selected
  useEffect(() => {
    if (selectedNumber && currentStep === 2) {
      fetchPackages();
    }
  }, [selectedNumber, currentStep]);

  const fetchRegularNumbers = async (searchZipcode?: string) => {
    try {
      setIsSearching(true);
      const response = await authApi.getAvailablePhoneNumbers({
        phoneNumberType: "regular",
        quantity: 30,
        zipcode: searchZipcode,
      });
      setRegularNumbers(response.data.phone_numbers);
    } catch (error) {
      console.error("Error fetching regular numbers:", error);
      toast.error("Failed to fetch regular numbers");
    } finally {
      setIsSearching(false);
    }
  };

  const fetchTollFreeNumbers = async () => {
    try {
      const response = await authApi.getAvailablePhoneNumbers({
        phoneNumberType: "toll_free",
        quantity: 30,
      });
      setTollFreeNumbers(response.data.phone_numbers);
    } catch (error) {
      console.error("Error fetching toll-free numbers:", error);
      toast.error("Failed to fetch toll-free numbers");
    }
  };

  const fetchPackages = async () => {
    try {
      const packageType =
        selectedType === "toll_free" ? "toll_free" : "regular";
      const response = await authApi.getPackages(packageType);
      setCallPackages(response.data.packages);
      if (response.data.packages.length > 0) {
        setSelectedMinutes(response.data.packages[0].minutes);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to fetch packages");
    }
  };

  const handleNumberSelect = () => {
    if (!selectedNumber) {
      toast.error("Please select a phone number");
      return;
    }
    setCurrentStep(2);
  };

  // Handler: after package selection, go to payment method confirmation (step 4)
  const handlePackageSelect = async () => {
    if (callPackages.length === 0) {
      toast.error("No packages available");
      return;
    }
    if (!selectedNumber) {
      toast.error("Please select a phone number");
      return;
    }
    setIsLoading(true);
    try {
      // Create reservation
      // Ensure phone number has proper format before sending to API
      const formattedPhoneNumber = ensurePhoneNumberFormat(selectedNumber);
      console.log("formattedPhoneNumber", formattedPhoneNumber);
      console.log("selectedNumber", selectedNumber);
      const { packagesApi } = await import("@/api/packagesApi");
      const reservationResponse = await packagesApi.createReservation(
        callPackages[selectedPackageIndex].id,
        formattedPhoneNumber
      );

      if (reservationResponse.data.reservation_id) {
        // Store reservation ID for step 3
        setReservationId(reservationResponse.data.reservation_id);
        setCurrentStep(3); // Go to step 3 for payment
        toast.success("Reservation created successfully!");
      } else {
        throw new Error("No reservation ID received");
      }
    } catch (error) {
      console.error("Error creating reservation:", error);
      toast.error("Failed to create reservation. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedNumber || callPackages.length === 0 || !reservationId) {
      toast.error("Please complete previous steps");
      return;
    }

    setIsLoading(true);
    try {
      const paymentResponse = await billingApi.pay(reservationId);

      console.log("Payment response:", paymentResponse);

      // After successful payment, create business line using reservation data
      let newBusinessLine = null;
      try {
        // Try to create business line from reservation data first
        newBusinessLine = await createBusinessLineFromReservation();
      } catch (reservationError) {
        console.error("Error getting reservation data:", reservationError);

        // Fallback: try to fetch from business lines API
        try {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const allLines = await businessLinesHooks.useActiveBusinessLines()
            .data;

          newBusinessLine = allLines.find(
            (line: any) => line.phone_number === selectedNumber
          );

          if (newBusinessLine) {
            console.log("Found new business line from API:", newBusinessLine);
          } else {
            throw new Error("Business line not found in API");
          }
        } catch (fetchError) {
          console.error(
            "Error fetching business line after payment:",
            fetchError
          );
          // Final fallback: create temporary business line
          newBusinessLine = {
            id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            business: "New Business Line",
            business_hour_data: {
              timezone: "America/New_York",
              working_hours: {
                monday: {
                  is_open: true,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                tuesday: {
                  is_open: true,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                wednesday: {
                  is_open: true,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                thursday: {
                  is_open: true,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                friday: {
                  is_open: true,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                saturday: {
                  is_open: false,
                  open_time: "09:00",
                  close_time: "17:00",
                },
                sunday: {
                  is_open: false,
                  open_time: "09:00",
                  close_time: "17:00",
                },
              },
            },
            is_default: false,
            line_type: selectedType,
            note: { content: null, expires_at: null },
            phone_number: selectedNumber,
            profile: { business_name: null, location: null },
            recording_enabled: false,
            service_type: "answering" as const,
            status: "pending-activation" as const,
          };
        }
      }

      onSuccess(newBusinessLine);

      // Wait 10 seconds then refetch business lines
      setTimeout(() => {
        refetchBusinessLines();
      }, 10000);

      onClose();
    } catch (error) {
      console.error("Error processing payment:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      toast.error(`Failed to process payment: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getBackgroundSize = () => {
    const min = 0;
    const max = callPackages.length - 1;
    const value = selectedPackageIndex;
    return { backgroundSize: `${((value - min) * 100) / (max - min)}% 100%` };
  };

  if (!isOpen) return null;

  // Determine which step content to show
  let stepContent: React.ReactNode = null;
  if (reservation && currentStep === 1) {
    const pkg = reservation.package;
    stepContent = (
      <>
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Number:</span>
            <span className="text-sm">{reservation.phone_number}</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Package:</span>
            <span className="text-sm">{pkg?.minutes} minutes</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Price:</span>
            <span className="text-sm font-bold">
              ${pkg ? pkg.price.toFixed(2) : "-"}
            </span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Expires:</span>
            <span className="text-sm">
              {new Date(reservation.expires_at).toLocaleString()}
            </span>
          </div>
        </div>
        <div className="flex justify-between gap-4">
          <button
            onClick={handleReservationDelete}
            disabled={isLoading}
            className="px-6 py-2 border border-red-300 rounded-lg text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
          >
            Delete
          </button>
          <button
            onClick={handleReservationPay}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 bg-[#0cb17b] text-white py-2 rounded-lg hover:bg-green-600 disabled:opacity-50"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Processing...</span>
              </div>
            ) : (
              <>
                <FiCreditCard size={16} />
                <span>Proceed to Payment</span>
              </>
            )}
          </button>
        </div>
      </>
    );
  } else if (currentStep === 4 && subscriptionId && reservationId) {
    const cards = paymentMethodsData || [];
    const defaultCard = cards.find((c: any) => c.is_default);
    stepContent = (
      <>
        <div className="mb-4 text-sm text-gray-700">
          {permissionLevel === "owner" ? (
            <>
              Please confirm your default payment method.
              <br />
              If you want to change your default card, go to{" "}
              <b>Settings &gt; Billing</b>.<br />
              <span className="text-xs text-gray-500">
                (subscription_id: {subscriptionId}, reservation_id:{" "}
                {reservationId})
              </span>
            </>
          ) : (
            <>
              Only the account owner can complete payments. Please contact the
              account owner to complete this purchase.
            </>
          )}
        </div>
        {permissionLevel === "owner" ? (
          isPaymentMethodsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#65C466] mx-auto mb-4"></div>
              <p className="text-gray-500">Loading payment methods...</p>
            </div>
          ) : defaultCard ? (
            <div className="flex items-center gap-4 border rounded-lg p-4 mb-6 bg-gray-50">
              <span className="text-lg font-bold text-blue-900">
                {defaultCard.card.display_brand || defaultCard.card.brand}
              </span>
              <span className="tracking-widest font-mono text-base">
                **** **** **** {defaultCard.card.last4}
              </span>
              <span className="text-xs text-gray-400 ml-2">
                Expires {defaultCard.card.exp_month}/{defaultCard.card.exp_year}
              </span>
              <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                Default
              </span>
            </div>
          ) : (
            <div className="text-red-500 mb-4">
              No default card found. Please add a card in settings.
            </div>
          )
        ) : null}
        <div className="flex justify-end gap-4">
          <button
            onClick={() => setCurrentStep(3)}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Back
          </button>
          {permissionLevel === "owner" ? (
            <button
              onClick={handlePayWithSubscription}
              disabled={isLoading || !defaultCard}
              className="flex-1 flex items-center justify-center gap-2 bg-[#0cb17b] text-white py-2 rounded-lg hover:bg-green-600 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                <>
                  <FiCreditCard size={16} />
                  <span>Pay</span>
                </>
              )}
            </button>
          ) : (
            <button
              disabled
              className="flex-1 flex items-center justify-center gap-2 bg-gray-300 text-gray-500 py-2 rounded-lg cursor-not-allowed"
            >
              <FiCreditCard size={16} />
              <span>Owner Only</span>
            </button>
          )}
        </div>
      </>
    );
  } else {
    // Steps 1-3 UI (existing code)
    stepContent = (
      <>
        {/* Step 1: Number Selection */}
        {currentStep === 1 && (
          <div>
            <p className="text-gray-500 text-sm mb-6">
              Please select your desired number.
            </p>

            <div className="flex mb-6">
              <button
                onClick={() => setSelectedType("regular")}
                className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                  selectedType === "regular"
                    ? "bg-[#65C466] text-white"
                    : "bg-white text-gray-600 border border-gray-200"
                }`}
              >
                Local DID
              </button>
              <button
                onClick={() => setSelectedType("toll_free")}
                className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                  selectedType === "toll_free"
                    ? "bg-[#65C466] text-white"
                    : "bg-white text-gray-600 border border-gray-200"
                }`}
              >
                Toll Free
              </button>
            </div>
            {selectedType === "regular" && (
              <div className="flex flex-col items-center mb-6 w-1/2 mx-auto">
                <div className="flex justify-center items-center border-b border-gray-200 w-full">
                  <FiSearch className="w-4 h-4 ml-2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="12345 or 12345-6789"
                    value={zipcode}
                    onChange={(e) => handleZipcodeChange(e.target.value)}
                    maxLength={10} // 5 digits + dash + 4 digits = 10 characters
                    className="w-full h-11 px-4 pl-10 text-sm focus:outline-none"
                  />
                  {isSearching && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#65C466] ml-2"></div>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Enter 5-digit zipcode or 9-digit ZIP+4 format
                </div>
              </div>
            )}
            {zipcode && (
              <div className="mb-4 text-center">
                <span className="text-sm text-gray-600">
                  Showing numbers for zipcode:{" "}
                  <span className="font-medium text-[#65C466]">{zipcode}</span>
                </span>
                <button
                  onClick={() => {
                    setZipcode("");
                    fetchRegularNumbers();
                  }}
                  className="ml-2 text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Clear search
                </button>
              </div>
            )}
            <div className="grid grid-cols-3 gap-4 max-h-[400px] overflow-y-auto">
              {selectedType === "regular" ? (
                <>
                  {/* Test Regular Numbers */}
                  {testRegularNumbers.map((phone, index) => (
                    <div
                      key={`test-regular-${index}`}
                      onClick={() =>
                        setSelectedNumber(
                          ensurePhoneNumberFormat(phone.full_number)
                        )
                      }
                      className={`flex border-2 border-dashed rounded-lg hover:border-[#65C466] transition-all cursor-pointer group ${
                        selectedNumber ===
                        ensurePhoneNumberFormat(phone.full_number)
                          ? "bg-orange-50 border-orange-300 text-orange-800"
                          : "bg-yellow-50 border-yellow-300"
                      }`}
                    >
                      {/* US Flag on the left */}
                      <div className="w-4 h-full rounded-l-lg overflow-hidden">
                        <img
                          src={usFlagImage}
                          alt="US Flag"
                          className="w-full h-full object-cover rounded-l-lg"
                        />
                      </div>
                      <div className="flex flex-col gap-1 p-3 w-full">
                        <div className="flex items-center gap-1">
                          <p
                            className={`text-sm font-medium ${
                              selectedNumber ===
                              ensurePhoneNumberFormat(phone.full_number)
                                ? "text-orange-800"
                                : "text-orange-700"
                            }`}
                          >
                            {formatPhoneNumberForDisplay(phone.full_number)}
                          </p>
                          <span className="text-xs bg-orange-200 text-orange-700 px-1 py-0.5 rounded">
                            TEST
                          </span>
                        </div>
                        <p
                          className={`text-xs ${
                            selectedNumber ===
                            ensurePhoneNumberFormat(phone.full_number)
                              ? "text-orange-600"
                              : "text-orange-500"
                          }`}
                        >
                          {phone.city}, {phone.state}
                        </p>
                        <div className="flex justify-between">
                          <p
                            className={`text-xs ${
                              selectedNumber ===
                              ensurePhoneNumberFormat(phone.full_number)
                                ? "text-orange-600"
                                : "text-orange-500"
                            }`}
                          >
                            {phone.rate_center}
                          </p>
                          <FiInfo
                            className={`w-4 h-4 ${
                              selectedNumber ===
                              ensurePhoneNumberFormat(phone.full_number)
                                ? "text-orange-500"
                                : "text-orange-400"
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Real Regular Numbers */}
                  {regularNumbers.length > 0 ? (
                    regularNumbers.map((phone, index) => (
                      <div
                        key={index}
                        onClick={() =>
                          setSelectedNumber(
                            ensurePhoneNumberFormat(phone.full_number)
                          )
                        }
                        className={`flex border rounded-lg hover:border-[#65C466] transition-all cursor-pointer group ${
                          selectedNumber ===
                          ensurePhoneNumberFormat(phone.full_number)
                            ? "bg-gray-200 border-gray-300 text-gray-800"
                            : "bg-white border-gray-100"
                        }`}
                      >
                        {/* US Flag on the left */}
                        <div className="w-4 h-full rounded-l-lg overflow-hidden">
                          <img
                            src={usFlagImage}
                            alt="US Flag"
                            className="w-full h-full object-cover rounded-l-lg"
                          />
                        </div>
                        <div className="flex flex-col gap-1 p-3 w-full">
                          <p
                            className={`text-sm font-medium ${
                              selectedNumber ===
                              ensurePhoneNumberFormat(phone.full_number)
                                ? "text-gray-800"
                                : "text-gray-900"
                            }`}
                          >
                            {formatPhoneNumberForDisplay(phone.full_number)}
                          </p>
                          <p
                            className={`text-xs ${
                              selectedNumber ===
                              ensurePhoneNumberFormat(phone.full_number)
                                ? "text-gray-600"
                                : "text-gray-500"
                            }`}
                          >
                            {phone.city}, {phone.state}
                          </p>
                          <div className="flex justify-between">
                            <p
                              className={`text-xs ${
                                selectedNumber ===
                                ensurePhoneNumberFormat(phone.full_number)
                                  ? "text-gray-600"
                                  : "text-gray-500"
                              }`}
                            >
                              {phone.rate_center}
                            </p>
                            <FiInfo
                              className={`w-4 h-4 ${
                                selectedNumber ===
                                ensurePhoneNumberFormat(phone.full_number)
                                  ? "text-gray-500"
                                  : "text-gray-400"
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-3 text-center py-8">
                      <p className="text-gray-500 mb-2">
                        {isSearching
                          ? "Searching..."
                          : "No phone numbers found"}
                      </p>
                      {zipcode && !isSearching && (
                        <p className="text-sm text-gray-400">
                          Try a different zipcode or clear the search
                        </p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Test Toll-Free Numbers */}
                  {testTollFreeNumbers.map((phone, index) => (
                    <div
                      key={`test-tollfree-${index}`}
                      onClick={() =>
                        setSelectedNumber(ensurePhoneNumberFormat(phone))
                      }
                      className={`flex border-2 border-dashed rounded-lg hover:border-[#65C466] transition-all cursor-pointer group ${
                        selectedNumber === ensurePhoneNumberFormat(phone)
                          ? "bg-purple-50 border-purple-300 text-purple-800"
                          : "bg-blue-50 border-blue-300"
                      }`}
                    >
                      {/* US Flag on the left */}
                      <div className="w-4 h-full rounded-l-lg overflow-hidden">
                        <img
                          src={usFlagImage}
                          alt="US Flag"
                          className="w-full h-full object-cover rounded-l-lg"
                        />
                      </div>
                      <div className="flex flex-col gap-1 p-3 w-full">
                        <div className="flex items-center gap-1">
                          <p
                            className={`text-sm font-medium ${
                              selectedNumber === ensurePhoneNumberFormat(phone)
                                ? "text-purple-800"
                                : "text-purple-700"
                            }`}
                          >
                            {phone}
                          </p>
                          <span className="text-xs bg-purple-200 text-purple-700 px-1 py-0.5 rounded">
                            TEST
                          </span>
                        </div>
                        <p
                          className={`text-xs ${
                            selectedNumber === ensurePhoneNumberFormat(phone)
                              ? "text-purple-600"
                              : "text-purple-500"
                          }`}
                        >
                          Toll Free Number
                        </p>
                        <div className="flex justify-between">
                          <p
                            className={`text-xs ${
                              selectedNumber === ensurePhoneNumberFormat(phone)
                                ? "text-purple-600"
                                : "text-purple-500"
                            }`}
                          ></p>
                          <FiInfo
                            className={`w-4 h-4 ${
                              selectedNumber === ensurePhoneNumberFormat(phone)
                                ? "text-purple-500"
                                : "text-purple-400"
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Real Toll-Free Numbers */}
                  {tollFreeNumbers.map((phone, index) => (
                    <div
                      key={index}
                      onClick={() =>
                        setSelectedNumber(ensurePhoneNumberFormat(phone))
                      }
                      className={`flex border rounded-lg hover:border-[#65C466] transition-all cursor-pointer group ${
                        selectedNumber === ensurePhoneNumberFormat(phone)
                          ? "bg-gray-200 border-gray-300 text-gray-800"
                          : "bg-white border-gray-100"
                      }`}
                    >
                      {/* US Flag on the left */}
                      <div className="w-4 h-full rounded-l-lg overflow-hidden">
                        <img
                          src={usFlagImage}
                          alt="US Flag"
                          className="w-full h-full object-cover rounded-l-lg"
                        />
                      </div>
                      <div className="flex flex-col gap-1 p-3 w-full">
                        <p
                          className={`text-sm font-medium ${
                            selectedNumber === ensurePhoneNumberFormat(phone)
                              ? "text-gray-800"
                              : "text-gray-900"
                          }`}
                        >
                          {phone}
                        </p>
                        <p
                          className={`text-xs ${
                            selectedNumber === ensurePhoneNumberFormat(phone)
                              ? "text-gray-600"
                              : "text-gray-500"
                          }`}
                        >
                          Toll Free Number
                        </p>
                        <div className="flex justify-between">
                          <p
                            className={`text-xs ${
                              selectedNumber === ensurePhoneNumberFormat(phone)
                                ? "text-gray-600"
                                : "text-gray-500"
                            }`}
                          ></p>
                          <FiInfo
                            className={`w-4 h-4 ${
                              selectedNumber === ensurePhoneNumberFormat(phone)
                                ? "text-gray-500"
                                : "text-gray-400"
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            <div className="mt-6 flex justify-center">
              <button
                onClick={handleNumberSelect}
                disabled={!selectedNumber}
                className={`w-[358px] py-2.5 rounded-lg font-medium text-sm ${
                  selectedNumber
                    ? "bg-[#65C466] text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Select
              </button>
            </div>
          </div>
        )}
        {/* Step 2: Package Selection */}
        {currentStep === 2 && (
          <div>
            <p className="text-sm text-[#7f828d] mb-6">
              After selecting a subscription, you can choose the number you
              want.
            </p>

            {callPackages.length > 0 ? (
              <>
                <div className="flex flex-col gap-10 bg-gradient-to-br from-[#00b67a] to-[#1dc7ff] p-6 rounded-2xl text-white text-center mb-6 w-1/2 mx-auto">
                  <div className="text-xl font-bold text-start">
                    $
                    {callPackages[selectedPackageIndex]?.price.toFixed(2) ||
                      "0.00"}
                  </div>
                  <div className="text-6xl font-bold text-center">
                    {selectedMinutes} min
                  </div>
                  <div className="text-sm text-end">
                    $
                    {callPackages[selectedPackageIndex]?.fee_per_overage_minute}{" "}
                    per add. min
                  </div>
                </div>

                <div className="relative w-full mb-8">
                  <style>
                    {`
                      input[type="range"] {
                        -webkit-appearance: none;
                        background-color: #edeff7;
                        border-radius: 8px;
                        height: 4px;
                        width: 100%;
                        background-image: linear-gradient(#0cb17b, #0cb17b);
                        background-repeat: no-repeat;
                      }

                      input[type="range"]::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        height: 20px;
                        width: 20px;
                        border-radius: 50%;
                        background: #0cb17b;
                        cursor: pointer;
                        box-shadow: 0 0 2px 0 rgb(0 0 0 / 10%);
                      }

                      input[type="range"]::-webkit-slider-runnable-track {
                        -webkit-appearance: none;
                        box-shadow: none;
                        border: none;
                        background: transparent;
                      }

                      .milestone {
                        width: 2px;
                        height: 10px;
                        background: #0cb17b;
                        position: absolute;
                        top: -3px;
                      }
                    `}
                  </style>
                  <div className="relative">
                    <input
                      aria-label="Select package"
                      type="range"
                      min={0}
                      max={callPackages.length - 1}
                      value={selectedPackageIndex}
                      onChange={(e) => {
                        const index = Number(e.target.value);
                        setSelectedPackageIndex(index);
                        setSelectedMinutes(callPackages[index].minutes);
                      }}
                      step={1}
                      style={getBackgroundSize()}
                      className="w-full"
                    />
                    {callPackages.map((_, index) => (
                      <div
                        aria-label="Package milestone"
                        key={index}
                        className="milestone"
                        style={{
                          left: `${(index / (callPackages.length - 1)) * 100}%`,
                        }}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-4">
                    {callPackages.map((pkg, index) => (
                      <div
                        key={pkg.id}
                        className={`text-xs ${
                          selectedPackageIndex === index
                            ? "text-[#0cb17b] font-bold"
                            : "text-[#aaacb3]"
                        }`}
                        style={{
                          position: "absolute",
                          left: `${(index / (callPackages.length - 1)) * 100}%`,
                          transform: "translateX(-50%)",
                        }}
                      >
                        {pkg.minutes}min
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={handlePackageSelect}
                    className="w-1/2 mx-auto text-center bg-[#0cb17b] text-white py-2 rounded-lg hover:bg-green-600"
                  >
                    Next
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#65C466] mx-auto mb-4"></div>
                <p className="text-gray-500">Loading packages...</p>
              </div>
            )}
          </div>
        )}
        {/* Step 3: Payment */}
        {currentStep === 3 && (
          <div>
            <p className="text-sm text-[#7f828d] mb-6">
              Complete your purchase to activate your business line. Your
              reservation has been created and is ready for payment.
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Selected Number:</span>
                <span className="text-sm">{selectedNumber}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Package:</span>
                <span className="text-sm">{selectedMinutes} minutes</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Price:</span>
                <span className="text-sm font-bold">
                  $
                  {callPackages[selectedPackageIndex]?.price.toFixed(2) ||
                    "0.00"}
                </span>
              </div>
              {reservationId && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Reservation ID:</span>
                  <span className="text-sm text-gray-500">{reservationId}</span>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <FiInfo
                  className="text-blue-600 mt-0.5 flex-shrink-0"
                  size={16}
                />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Payment Processing</p>
                  <p>
                    Your payment will be processed securely through our payment
                    gateway. You may be redirected to complete the payment
                    process.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handlePayment}
                disabled={isLoading}
                className={`w-1/2 mx-auto text-center py-2 rounded-lg font-medium ${
                  isLoading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-[#0cb17b] text-white hover:bg-green-600"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processing Payment...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <FiCreditCard size={16} />
                    <span>Proceed to Payment</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50">
      <div
        className={`bg-white rounded-2xl w-full ${
          reservation || (currentStep === 4 && subscriptionId && reservationId)
            ? "max-w-[500px]"
            : "max-w-[800px]"
        } p-8 shadow-lg relative max-h-[90vh] overflow-y-auto`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex flex-col gap-2">
            <div className="w-5 h-5">
              <FiPhone className="text-[#65C466]" size={20} />
            </div>
            <h2 className="text-lg font-medium">
              {reservation
                ? "Reserved Number"
                : currentStep === 1
                ? "Select your number"
                : currentStep === 2
                ? "Select your subscription"
                : currentStep === 3
                ? "Complete Payment"
                : currentStep === 4
                ? "Confirm Payment"
                : ""}
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close dialog"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Progress Steps (not for reservation) */}
        {!reservation && (
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-4">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep >= step
                        ? "bg-[#65C466] text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {step}
                  </div>
                  {step < 4 && (
                    <div
                      className={`w-12 h-1 mx-2 ${
                        currentStep > step ? "bg-[#65C466]" : "bg-gray-200"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step Content */}
        {stepContent}
      </div>

      {/* Payment Issue Dialog */}
      {showPaymentIssueDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <FiAlertTriangle className="text-red-600" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Payment Issue
              </h3>
            </div>

            <p className="text-gray-600 mb-6">{paymentIssueError}</p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPaymentIssueDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  window.location.href =
                    "/settings/reports?openCardDialog=true";
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Resolve Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
